import {
  useEffect,
  useEffectEvent,
  useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  parseRealtimeEvent,
  realtimeApi,
} from "../lib/realtime";
import type { GigRequestMessageResponse } from "../types/gigTypes";

type RealtimeProviderProps = {
  children: ReactNode;
};

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

function mergeMessage(
  existing: GigRequestMessageResponse[] | undefined,
  incoming: GigRequestMessageResponse,
): GigRequestMessageResponse[] | undefined {
  if (!existing) {
    return existing;
  }

  if (existing.some((message) => message.id === incoming.id)) {
    return existing;
  }

  return [...existing, incoming].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export const RealtimeProvider = ({ children }: RealtimeProviderProps) => {
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const shouldReconnectRef = useRef(false);

  const clearReconnectTimer = useEffectEvent(() => {
    if (reconnectTimerRef.current === null) {
      return;
    }
    window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
  });

  const closeSocket = useEffectEvent(() => {
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) {
      socket.close();
    }
  });

  const scheduleReconnect = useEffectEvent(() => {
    if (!shouldReconnectRef.current || reconnectTimerRef.current !== null) {
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttemptRef.current,
      RECONNECT_MAX_DELAY_MS,
    );

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      reconnectAttemptRef.current += 1;
      void connect();
    }, delay);
  });

  const handleRealtimeEvent = useEffectEvent((payload: string) => {
    const event = parseRealtimeEvent(payload);
    if (!event) {
      return;
    }

    if (event.type === "gig_request_message.created") {
      for (const role of ["teen", "adult"] as const) {
        queryClient.setQueryData<GigRequestMessageResponse[] | undefined>(
          ["gig-request-messages", role, event.message.requestId],
          (current) => mergeMessage(current, event.message),
        );
      }
    }
  });

  const connect = useEffectEvent(async () => {
    if (!shouldReconnectRef.current || socketRef.current) {
      return;
    }

    clearReconnectTimer();

    try {
      const session = await realtimeApi.createSession();
      if (!shouldReconnectRef.current) {
        return;
      }

      const websocketUrl = session.websocketUrl?.trim();
      const ticket = session.ticket?.trim();
      if (!websocketUrl || !ticket) {
        return;
      }

      const url = new URL(websocketUrl);
      url.searchParams.set("ticket", ticket);

      const socket = new WebSocket(url.toString());
      socketRef.current = socket;

      socket.addEventListener("open", () => {
        reconnectAttemptRef.current = 0;
      });

      socket.addEventListener("message", (event) => {
        if (typeof event.data === "string") {
          handleRealtimeEvent(event.data);
        }
      });

      socket.addEventListener("close", () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (shouldReconnectRef.current) {
          scheduleReconnect();
        }
      });

      socket.addEventListener("error", () => {
        socket.close();
      });
    } catch {
      if (shouldReconnectRef.current) {
        scheduleReconnect();
      }
    }
  });

  useEffect(() => {
    shouldReconnectRef.current = isAuthenticated && Boolean(user?.id);

    if (shouldReconnectRef.current) {
      void connect();
    } else {
      clearReconnectTimer();
      reconnectAttemptRef.current = 0;
      closeSocket();
    }

    return () => {
      shouldReconnectRef.current = false;
      clearReconnectTimer();
      reconnectAttemptRef.current = 0;
      closeSocket();
    };
  }, [clearReconnectTimer, closeSocket, connect, isAuthenticated, user?.id]);

  useEffect(() => {
    const handleOnline = () => {
      if (!shouldReconnectRef.current) {
        return;
      }
      reconnectAttemptRef.current = 0;
      clearReconnectTimer();
      closeSocket();
      void connect();
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [clearReconnectTimer, closeSocket, connect]);

  return children;
};
